import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';
import Typography from '@material-ui/core/Typography';
import get from 'lodash/get';
import find from 'lodash/find';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import CircularProgress from '@material-ui/core/CircularProgress';
import clsx from 'clsx';
import { useMediaQuery } from '@material-ui/core';
import Magnifier from './Magnifier';
import Filter from './Filter';
import { getOptionColors } from '../../Reducers/Configurator/Selectors';

const useStyles = makeStyles(
  (theme) => ({
    title: {
      margin: '24px 0px 24px 24px',
      [theme.breakpoints.down('sm')]: {
        margin: '12px 0px 8px 12px',
      },
    },
    root: {
      maxWidth: '100%',
      width: 330,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    loader: {
      position: 'absolute',
      left: '50%',
      top: '50%',
    },
    optionsContainer: {
      marginRight: 'auto',
      maxHeight: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      overflowX: 'hidden',
      overflowY: 'auto',
      cursor: 'pointer',
      paddingRight: 15,
      [theme.breakpoints.down('xs')]: {
        display: 'flex',
        flexWrap: 'wrap',
      },
    },
    thumbnailImage: {
      width: 130,
      maxWidth: '100%',
      aspectRatio: 1,
    },
    optionContainer: {
      maxWidth: 130,
      margin: '0px 0px 16px 12px',
      color: '#ffff',
      padding: 5,
      [theme.breakpoints.down('xs')]: {
        maxWidth: 'calc(50% - 22px)',
      },
    },
    selectedOptionContainer: {
      backgroundColor: theme.palette.primary.main,
      '& p': {
        color: '#fff',
      },
    },
    optionTitle: {
      fontWeight: 'bold',
      marginRight: 5,
    },
    selectedOption: {
      textDecoration: 'underline',
      textDecorationColor: theme.palette.primary.main,
    },
    optionPrice: {},
    variants: {},
    colorTitle: {
      wordBreak: 'break-all',
    },
    filter: {
      gridColumn: '1 / 3',
    },
  }),
  { name: 'ProductPartsList' }
);

const ProductPartsListComponent = ({ options, onSelectOption, colorFilterItems, selectedOption }) => {
  const isSmallHeight = useMediaQuery('(max-height:840px)');
  const classes = useStyles({ isSmallHeight });
  const [colorFilterValue, setColorFilterValue] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  useEffect(() => {
    if (colorFilterValue) {
      return;
    }
    if (selectedOption) {
      const relatedColorFilters = getOptionColors(find(options, { identifier: selectedOption.identifier }));
      if (relatedColorFilters.length && colorFilterItems?.length) {
        const selectedColorFilter = find(colorFilterItems, { id: get(relatedColorFilters, '[0].id') });
        if (selectedColorFilter) {
          setColorFilterValue(selectedColorFilter);
          return;
        }
      }
    }
    colorFilterItems?.length && setColorFilterValue(colorFilterItems[0]);
  }, [colorFilterItems, selectedOption, options, colorFilterValue]);
  useEffect(() => {
    setFilteredOptions(
      options.filter((option) => {
        const colors = getOptionColors(option);
        if (!colors.length) {
          return true;
        }
        return colors.some(({ id }) => id === colorFilterValue.id);
      })
    );
  }, [colorFilterValue, options]);

  return (
    <>
      <div className={classes.root}>
        <Typography className={classes.title} variant="body1">
          {t('productPartsList.materialHeaderTitle')}
        </Typography>
        {!isSmallHeight && (
          <Filter items={colorFilterItems} onSelect={setColorFilterValue} value={colorFilterValue} />
        )}
        <div className={classes.optionsContainer}>
          {isSmallHeight && (
            <Filter
              items={colorFilterItems}
              onSelect={setColorFilterValue}
              value={colorFilterValue}
              className={classes.filter}
              smallMode
            />
          )}
          {options.length === 0 && <CircularProgress className={classes.loader} />}
          {filteredOptions.map((option, i) => {
            const isSelected = selectedOption && option.identifier === selectedOption.identifier;
            return (
              option.identifier && (
                <div
                  className={clsx(classes.optionContainer, isSelected && classes.selectedOptionContainer)}
                  key={i}
                  onClick={() => onSelectOption({ ...option, amount: 1 })}
                >
                  <Magnifier
                    thumbnailSrc={getImageUrl(get(option, 'thumbnail', ''))}
                    src={
                      getImageUrl(get(option, 'detailImage', '')) || getImageUrl(get(option, 'thumbnail', ''))
                    }
                    thumbnailClassName={classes.thumbnailImage}
                    alt={option.title}
                  />
                  <Typography variant={'body2'} className={classes.optionTitle}>
                    {option.title}
                  </Typography>
                </div>
              )
            );
          })}
        </div>
      </div>
    </>
  );
};

ProductPartsListComponent.defaultProps = {
  options: [],
  selectedOption: null,
  colorFilterItems: [],
};

ProductPartsListComponent.propTypes = {
  options: PropTypes.array,
  selectedOption: PropTypes.object,
  onSelectOption: PropTypes.func,
  colorFilterItems: PropTypes.array,
};

export default ProductPartsListComponent;
